import { Box, Button, Grid, Tab, Theme, Typography, useTheme } from '@mui/material';
import React, { useMemo, useRef } from 'react';
import BreadCrum from '../../../lib/BreadCrum';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { RootStateType } from '../../../redux-store/reducers';
import { TabContext, TabList } from '@mui/lab';
import { Formik, FormikProps, FormikValues, useFormikContext } from '../../../lib/formik/Formik';
import { isModuleAllowed, toINR } from '../../../utils/utilityFunctions';
import CustomePieChart from '../../dashboard/chart/CustomePieChart';
import { CustomSlider } from '../../../lib/formik/CustomSlider';
import { USER_ROLES } from '../../../utils/constant';
import { navigationData } from '../types';

type ActiveTab = 'Lumpsum' | 'SIP' | 'Lumpsum+SIP';

type GoalInput =
    | { tab: 'Lumpsum'; goal: number; expectedReturnPA: number; years: number }
    | { tab: 'SIP'; goal: number; expectedReturnPA: number; years: number }
    | { tab: 'Lumpsum+SIP'; goal: number; lumpsumNow: number; expectedReturnPA: number; years: number };

type MoneyOut = { value: number; currency: string };
type GoalOutput = {
    labelPrimary: string;
    required: string;
    invested: MoneyOut;
    returns: MoneyOut;
    total: MoneyOut;
    comboMonthlySip?: { value: number; currency: string };
    comboQuarterlySip?: { value: number; currency: string };
};

// ---- Form initial values ----
const InitialValues: FormikValues = {
    activeTab: 'Lumpsum',
    goalLumpsum: { goalAmount: 10000000, expectedReturn: 12, timePeriod: 10 },
    goalSip: { goalAmount: 10000000, expectedReturn: 12, timePeriod: 10 },
    goalCombo: { goalAmount: 10000000, lumpsumNow: 2000000, expectedReturn: 12, timePeriod: 10 },
};

// ---- Finance helpers ----
// Monthly effective rate from annual rate (EAR to monthly effective)
const toMonthlyRate = (annualPct: number) =>
    Math.pow(1 + annualPct / 100, 1 / 12) - 1;

// Future value of a single lump sum invested today for n years at annual r
const fvOfNowLumpsum = (pvNow: number, rAnnualPct: number, years: number) =>
    pvNow * Math.pow(1 + rAnnualPct / 100, years);

// Present value required today to reach a target FV in n years at annual r
const pvRequiredNowForFV = (fv: number, rAnnualPct: number, years: number) =>
    fv / Math.pow(1 + rAnnualPct / 100, years);

// Future value of a monthly ordinary SIP (end-of-month)
const fvSipOrdinary = (pmtMonthly: number, rAnnualPct: number, years: number) => {
    const i = toMonthlyRate(rAnnualPct);
    const n = years * 12;
    if (i === 0) return pmtMonthly * n;
    return pmtMonthly * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
};

// Inverse: required monthly SIP for a target FV
const reqMonthlySipForGoal = (fvGoal: number, rAnnualPct: number, years: number) => {
    const i = toMonthlyRate(rAnnualPct);
    const n = years * 12;
    if (n === 0) return 0;
    if (i === 0) return fvGoal / n;
    const factor = ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    return fvGoal / factor;
};

// Effective quarterly rate from annual EAR
const toQuarterlyRate = (annualPct: number) =>
    Math.pow(1 + annualPct / 100, 1 / 4) - 1;

// Required periodic payment for target FV (ordinary annuity, end-of-period)
const reqPeriodicForFV = (fvGoal: number, i: number, n: number) => {
    if (n === 0) return 0;
    if (i === 0) return fvGoal / n;
    const factor = ((Math.pow(1 + i, n) - 1) / i);
    return fvGoal / factor;
};


// ---- Calculator logic (pure) ----
function calculateGoal(
    input: GoalInput,
    format: (x: { value: number; locale: string; currencyCode: string }) => string,
    locale: string,
    currencyCode: string

): GoalOutput {
    if (input.tab === 'Lumpsum') {
        const FV = input.goal;
        const r = input.expectedReturnPA;
        const y = input.years;

        // Required one-time lumpsum now (present value)
        const pvNow = pvRequiredNowForFV(FV, r, y);
        const invested = pvNow;
        const returns = Math.max(0, Math.round(FV - invested));
        const total = FV;

        return {
            labelPrimary: 'Required One-time Lumpsum (now)',
            required: format({ value: pvNow, locale, currencyCode }),
            invested: { value: invested, currency: format({ value: invested, locale, currencyCode }) },
            returns: { value: returns, currency: format({ value: returns, locale, currencyCode }) },
            total: { value: total, currency: format({ value: total, locale, currencyCode }) },
        };
    }

    if (input.tab === 'SIP') {
        const FV = input.goal;
        const r = input.expectedReturnPA;
        const y = input.years;

        const pmt = reqMonthlySipForGoal(FV, r, y);
        const invested = pmt * 12 * y;
        const returns = Math.max(0, Math.round(FV - invested));
        const total = FV;

        return {
            labelPrimary: 'Required Monthly SIP',
            required: format({ value: pmt, locale, currencyCode }),
            invested: { value: invested, currency: format({ value: invested, locale, currencyCode }) },
            returns: { value: returns, currency: format({ value: returns, locale, currencyCode }) },
            total: { value: total, currency: format({ value: total, locale, currencyCode }) },
        };
    }

    // Lumpsum+SIP: one-time lumpsum now + monthly SIP for the residual
    const FV = input.goal;
    const r = input.expectedReturnPA;
    const y = input.years;
    const L0 = input.lumpsumNow;

    // Future value contributed by the one-time lumpsum now
    const fvFromNowLumpsum = fvOfNowLumpsum(L0, r, y);
    const fvResidual = Math.max(0, FV - fvFromNowLumpsum);
    const pmt = reqMonthlySipForGoal(fvResidual, r, y);

    const iq = toQuarterlyRate(r);
    const nq = y * 4;
    const pmtQuarterly = reqPeriodicForFV(fvResidual, iq, nq);

    const invested = L0 + pmt * 12 * y;
    const returns = Math.max(0, Math.round(FV - invested));
    const total = FV;

    return {
        labelPrimary: 'Required Monthly SIP (with one-time lumpsum now)',
        required: format({ value: pmt, locale, currencyCode }),
        invested: { value: invested, currency: format({ value: invested, locale, currencyCode }) },
        returns: { value: returns, currency: format({ value: returns, locale, currencyCode }) },
        total: { value: total, currency: format({ value: total, locale, currencyCode }) },
        comboMonthlySip: { value: pmt, currency: format({ value: pmt, locale, currencyCode }) },
        comboQuarterlySip: { value: pmtQuarterly, currency: format({ value: pmtQuarterly, locale, currencyCode }) },
    };
}

// ---- Calculator UI ----
const Calculator = ({ activeTab }: { activeTab: ActiveTab }) => {
    const theme = useTheme();
    const { values, errors }: FormikProps<FormikValues> = useFormikContext();
    const { locale, currencyCode } = useSelector((state: RootStateType) => state.currencyInfo);

    const tab: ActiveTab = activeTab;

    const goalAmount =
        tab === 'Lumpsum'
            ? values?.goalLumpsum?.goalAmount ?? 0
            : tab === 'SIP'
                ? values?.goalSip?.goalAmount ?? 0
                : values?.goalCombo?.goalAmount ?? 0;

    const expectedReturnPA =
        tab === 'Lumpsum'
            ? values?.goalLumpsum?.expectedReturn ?? 0
            : tab === 'SIP'
                ? values?.goalSip?.expectedReturn ?? 0
                : values?.goalCombo?.expectedReturn ?? 0;

    const years =
        tab === 'Lumpsum'
            ? values?.goalLumpsum?.timePeriod ?? 0
            : tab === 'SIP'
                ? values?.goalSip?.timePeriod ?? 0
                : values?.goalCombo?.timePeriod ?? 0;

    const lumpsumNow =
        tab === 'Lumpsum+SIP' ? values?.goalCombo?.lumpsumNow ?? 0 : 0;

    // Error gate
    const hasErrors = useMemo(() => {
        const e: any = errors;
        return Boolean(
            e?.goalLumpsum?.goalAmount ||
            e?.goalLumpsum?.expectedReturn ||
            e?.goalLumpsum?.timePeriod ||
            e?.goalSip?.goalAmount ||
            e?.goalSip?.expectedReturn ||
            e?.goalSip?.timePeriod ||
            e?.goalCombo?.goalAmount ||
            e?.goalCombo?.lumpsumNow ||
            e?.goalCombo?.expectedReturn ||
            e?.goalCombo?.timePeriod
        );
    }, [errors]);

    const calc = useMemo(() => {
        if (hasErrors) {
            const zero = { value: 0, currency: toINR({ value: 0, locale, currencyCode }) };
            return {
                labelPrimary: '-',
                required: toINR({ value: 0, locale, currencyCode }),
                invested: zero,
                returns: zero,
                total: zero,
            } as GoalOutput;
        }
        const input: GoalInput =
            tab === 'Lumpsum'
                ? { tab: 'Lumpsum', goal: Number(goalAmount), expectedReturnPA: Number(expectedReturnPA), years: Number(years) }
                : tab === 'SIP'
                    ? { tab: 'SIP', goal: Number(goalAmount), expectedReturnPA: Number(expectedReturnPA), years: Number(years) }
                    : {
                        tab: 'Lumpsum+SIP',
                        goal: Number(goalAmount),
                        lumpsumNow: Number(lumpsumNow),
                        expectedReturnPA: Number(expectedReturnPA),
                        years: Number(years),
                    };
        return calculateGoal(input, toINR, locale, currencyCode);
    }, [hasErrors, tab, goalAmount, expectedReturnPA, years, lumpsumNow, locale, currencyCode]);

    const estimatedReturnColor = theme.palette.primary[500] ?? '#1BBAC8';
    const investedAmountColor = theme.palette.primary[800] ?? '#114f8c';
    const colorArray = [estimatedReturnColor, investedAmountColor];

    const pieData = useMemo(
        () => [
            ['Est. Return', calc.returns.value],
            ['Invested Amount', calc.invested.value],
        ],
        [calc.returns.value, calc.invested.value]
    );

    const totalValue = useMemo(
        () => ({ label: 'Total Value', value: calc.total.value }),
        [calc.total.value]
    );

    const chartKey = useMemo(
        () => `${calc.returns.value}-${calc.invested.value}-${calc.total.value}`,
        [calc.returns.value, calc.invested.value, calc.total.value]
    );

    return (
        <Grid container columnSpacing={{ xs: 0, md: 3 }} rowSpacing={{ xs: 4, md: 6 }} alignItems="flex-start">
            {/* Left: sliders */}
            <Grid item xs={12} md={7}>
                <Grid container direction="column" rowSpacing={5}>
                    {/* Goal Amount */}
                    <Grid item>
                        <CustomSlider
                            name={
                                tab === 'Lumpsum'
                                    ? 'goalLumpsum.goalAmount'
                                    : tab === 'SIP'
                                        ? 'goalSip.goalAmount'
                                        : 'goalCombo.goalAmount'
                            }
                            label="Goal Amount"
                            max={100000000}
                            inputMode="numeric"
                            startCharacter="₹"
                            sx={{ maxWidth: 160 }}
                        />
                    </Grid>

                    {/* Lumpsum now (only for combo tab) */}
                    {tab === 'Lumpsum+SIP' && (
                        <Grid item>
                            <CustomSlider
                                name="goalCombo.lumpsumNow"
                                label="One-time Lumpsum (now)"
                                max={100000000}
                                inputMode="numeric"
                                startCharacter="₹"
                                sx={{ maxWidth: 160 }}
                            />
                        </Grid>
                    )}

                    {/* Expected Return */}
                    <Grid item>
                        <CustomSlider
                            name={
                                tab === 'Lumpsum'
                                    ? 'goalLumpsum.expectedReturn'
                                    : tab === 'SIP'
                                        ? 'goalSip.expectedReturn'
                                        : 'goalCombo.expectedReturn'
                            }
                            label="Expected return rate (p.a)"
                            max={30}
                            endCharacter="%"
                            inputMode="decimal"
                        />
                    </Grid>

                    {/* Time Period */}
                    <Grid item>
                        <CustomSlider
                            name={
                                tab === 'Lumpsum'
                                    ? 'goalLumpsum.timePeriod'
                                    : tab === 'SIP'
                                        ? 'goalSip.timePeriod'
                                        : 'goalCombo.timePeriod'
                            }
                            label="Time Period"
                            max={40}
                            endCharacter="Yr"
                            inputMode="numeric"
                        />
                    </Grid>

                    <Box sx={(theme: Theme) => ({ border: `0.1px dotted ${theme.palette.grey[400]}B2`, mt: 3 })} />
                    <Box sx={{ mt: 3 }} />

                    {/* Required contribution display */}
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs>
                            <Typography variant="body_500" sx={{ color: 'text.secondary' }}>
                                {calc.labelPrimary}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body_600">{calc.required}</Typography>
                        </Grid>
                    </Grid>
                    {activeTab === 'Lumpsum+SIP' && (
                        <Grid container mt={2}>
                            <Grid item xs>
                                <Typography variant='body_500' sx={{ color: "text.secondary" }}>Required Quarterly SIP</Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant='body_600'>{calc.comboQuarterlySip?.currency ?? '-'}</Typography>
                            </Grid>
                        </Grid>
                    )}

                    {/* Invested, Returns, Total */}
                    <Grid container mt={2}>
                        <Grid item xs>
                            <Typography variant="body_500" sx={{ color: 'text.secondary' }}>
                                Invested Amount
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body_600">{calc.invested.currency}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container mt={2}>
                        <Grid item xs>
                            <Typography variant="body_500" sx={{ color: 'text.secondary' }}>
                                Est Return
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body_600">{calc.returns.currency}</Typography>
                        </Grid>
                    </Grid>
                    <Grid container mt={2}>
                        <Grid item xs>
                            <Typography variant="body_500" sx={{ color: 'text.secondary' }}>
                                Total Value
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Typography variant="body_600">{calc.total.currency}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Right: chart and CTA */}
            <Grid
                container
                item
                xs={12}
                md={5}
                direction="column"
                rowSpacing={4}
                alignItems="center"
                justifyContent="center"
                width={'100%'}
                maxWidth={'100%'}
            >
                <Grid item container justifyContent="center" columnSpacing={6} sx={{ width: '100%', flexWrap: 'nowrap' }}>
                    <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.500' }} />
                        <Typography variant="body_500">Est Return</Typography>
                    </Grid>
                    <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'primary.800' }} />
                        <Typography variant="body_500">Invested Amount</Typography>
                    </Grid>
                </Grid>

                <Grid item sx={{ width: '100%' }}>
                    {!hasErrors && (
                        <CustomePieChart
                            key={chartKey}
                            data={pieData}
                            isLoading={false}
                            labelData={[]}
                            totalValue={totalValue}
                            showSlider={false}
                            align='center'
                            colorArr={colorArray}
                            labelOrderEnabled
                            title="Goal Calculator"
                        />
                    )}
                </Grid>

                <Grid item>
                    <Button
                        variant="contained"
                        sx={{ px: 5, py: 1, bgcolor: 'primary.500', borderRadius: 2, border: 'none', cursor: 'pointer' }}
                        onClick={() => history.back()}
                    >
                        <Typography variant="body_500">Invest Now</Typography>
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

// ---- Page component ----
export default function GoalCalculator(): JSX.Element {
    const history = useHistory();
    const { pathname: pathName, state } = history.location;
    const { clientCode = '' } = state as navigationData

    const {
        role = '',
        permissions = []
    } = useSelector((store: RootStateType) => store.auth);
    const {
        selectedPortfolio,
    } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const theme = useTheme();
    const initialRouter = pathName.split('/').filter((item) => item !== '');
    const tabList: ActiveTab[] = ['Lumpsum', 'SIP', 'Lumpsum+SIP'];
    const formikRef = useRef<FormikProps<any>>(null);

    const rmRouterPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard'
        },
        {
            name: initialRouter[1] + `${selectedPortfolio.label ? ` (${selectedPortfolio.label})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode
            }
        },
        {
            name: 'Mutual Fund',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode
            }
        },
        {
            name: 'Explore Mutual Funds',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
            state: {
                clientCode
            }
        },
        {
            name: 'Goal Calculator',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}/${initialRouter[4]}`,
            state: {
                clientCode
            }
        },
    ] : []);

    const investorRouterPath = [
        {
            name: 'Dashboard' + `${selectedPortfolio.label ? ` (${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard'
        },
        {
            name: 'Mutual Fund',
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode
            }
        },
        {
            name: 'Explore Mutual Funds',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode
            }
        },
        {
            name: 'Goal Calculator',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}/${initialRouter[3]}`,
            state: {
                clientCode
            }
        },
    ];

    const handleSubmit = () => {
        console.log('submit');
    };

    const handleChange = (_event: React.SyntheticEvent, newValue: ActiveTab) => {
        formikRef.current?.setFieldValue('activeTab', newValue);
    };

    // Validation updated: combo uses lumpsumNow instead of yearlyLumpsum; all are numeric strings allowed by CustomSlider
    const validationSchema = yup.object().shape({
        activeTab: yup.string().required('Active tab is required'),
        goalLumpsum: yup.object().shape({
            goalAmount: yup
                .string()
                .test('goalAmount', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'Lumpsum') return true;
                    if (!fieldValue) return createError({ message: 'Goal amount is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Goal amount can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Goal amount should be a valid number', path });
                    return true;
                }),
            expectedReturn: yup
                .string()
                .test('expectedReturn', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'Lumpsum') return true;
                    if (!fieldValue) return createError({ message: 'Expected return is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Expected return can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Expected return should be a valid number', path });
                    return true;
                }),
            timePeriod: yup
                .string()
                .test('timePeriod', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'Lumpsum') return true;
                    if (!fieldValue) return createError({ message: 'Time period is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Time period can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Time period should be a valid number', path });
                    return true;
                }),
        }),
        goalSip: yup.object().shape({
            goalAmount: yup
                .string()
                .test('goalAmount', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'SIP') return true;
                    if (!fieldValue) return createError({ message: 'Goal amount is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Goal amount can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Goal amount should be a valid number', path });
                    return true;
                }),
            expectedReturn: yup
                .string()
                .test('expectedReturn', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'SIP') return true;
                    if (!fieldValue) return createError({ message: 'Expected return is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Expected return can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Expected return should be a valid number', path });
                    return true;
                }),
            timePeriod: yup
                .string()
                .test('timePeriod', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'SIP') return true;
                    if (!fieldValue) return createError({ message: 'Time period is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Time period can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Time period should be a valid number', path });
                    return true;
                }),
        }),
        goalCombo: yup.object().shape({
            goalAmount: yup
                .string()
                .test('goalAmount', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'Lumpsum+SIP') return true;
                    if (!fieldValue) return createError({ message: 'Goal amount is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Goal amount can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Goal amount should be a valid number', path });
                    return true;
                }),
            lumpsumNow: yup
                .string()
                .test('lumpsumNow', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'Lumpsum+SIP') return true;
                    if (!fieldValue) return createError({ message: 'One-time lumpsum is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "One-time lumpsum can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'One-time lumpsum should be a valid number', path });
                    return true;
                }),
            expectedReturn: yup
                .string()
                .test('expectedReturn', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'Lumpsum+SIP') return true;
                    if (!fieldValue) return createError({ message: 'Expected return is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Expected return can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Expected return should be a valid number', path });
                    return true;
                }),
            timePeriod: yup
                .string()
                .test('timePeriod', (value, context) => {
                    const fieldValue = value === null ? '' : value;
                    const { createError, path, options } = context;
                    const { context: optionsContext = {} } = options;
                    if (`${optionsContext?.activeTab ?? ''}` !== 'Lumpsum+SIP') return true;
                    if (!fieldValue) return createError({ message: 'Time period is required', path });
                    if (fieldValue?.includes('-') || fieldValue?.includes('+')) return createError({ message: "Time period can't have invalid characters", path });
                    if (Number.isNaN(Number(fieldValue))) return createError({ message: 'Time period should be a valid number', path });
                    return true;
                }),
        }),
    });

    return (
        <Box>
            <BreadCrum array={![USER_ROLES.INVESTOR].includes(role) ? rmRouterPath : investorRouterPath} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', marginTop: '10px' }}>
                <Typography variant="h1" sx={(theme: Theme) => ({ color: theme.palette.text.primary, mb: { xs: '10px', sm: '0px' } })}>
                    Goal Calculator
                </Typography>
            </Box>
            <Box
                sx={(theme: Theme) => ({
                    marginTop: '25px',
                    borderRadius: '25px',
                    backgroundColor: theme.palette.common.white,
                    boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`,
                    padding: '18px',
                })}
            >
                <Formik
                    initialValues={InitialValues}
                    enableReinitialize={true}
                    onSubmit={() => { console.log('submit') }}
                    innerRef={formikRef}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                >
                    {({ values }) => {
                        return (
                            <TabContext value={values.activeTab}>
                                <Box
                                    sx={(theme: Theme) => ({
                                        borderBottom: `1px solid ${theme.palette.text.secondary}99`,
                                        display: 'flex',
                                        flexDirection: 'row',
                                        paddingBottom: { lg: '0px', md: '0px', sm: '0.1px', xs: '0.1px' },
                                        [theme.breakpoints.down('sm')]: {
                                            overflowY: 'auto',
                                            scrollbarWidth: 'auto',
                                            '&::-webkit-scrollbar': {
                                                height: '8px',
                                                backgroundColor: theme.palette.grey[200],
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: theme.palette.primary.main,
                                                borderRadius: '9px',
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                backgroundColor: theme.palette.primary.dark,
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: theme.palette.background.paper,
                                            },
                                        },
                                    })}
                                >
                                    <TabList
                                        sx={(theme: Theme) => ({
                                            '& .MuiTabs-indicator': { display: 'none' },
                                            minHeight: 28,
                                            height: 28,
                                            [theme.breakpoints.down('sm')]: {
                                                display: 'flex',
                                                flexShrink: 0,
                                            },
                                        })}
                                        {...values}
                                        onChange={(_, v) => handleChange(_, v as ActiveTab)}
                                    >
                                        {tabList.map((item, index) => {
                                            return (
                                                <Tab
                                                    label={
                                                        <Typography
                                                            variant="body_500"
                                                            sx={(theme: Theme) => ({
                                                                color: values.activeTab === item ? theme.palette.common.white : theme.palette.common.black,
                                                                textTransform: 'capitalize',
                                                            })}
                                                        >
                                                            {item}
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        minHeight: 28,
                                                        height: 28,
                                                        px: { xs: 5, sm: 1.5 },
                                                        py: { xs: 0, sm: 1.5 },
                                                        mr: 2,
                                                        borderRadius: '7px 7px 0px 0px',
                                                        background: values.activeTab === item ? theme.palette.primary[500] : theme.palette.grey[500],
                                                    }}
                                                    key={index}
                                                    value={item}
                                                />
                                            );
                                        })}
                                    </TabList>
                                </Box>
                                <Box sx={{ mt: 4 }}>
                                    <Calculator activeTab={values.activeTab} />
                                </Box>
                            </TabContext>
                        );
                    }}
                </Formik>
            </Box>
        </Box>
    );
}
